import React from 'react';
import { Dialog } from '@progress/kendo-react-dialogs';
import { Button } from '@progress/kendo-react-buttons';
import { Input as KInput } from '@progress/kendo-react-inputs';
import { TaskBoard, TaskBoardToolbar } from '@progress/kendo-react-taskboard';
import { createGlobalStyle } from 'styled-components';
import { Card as CustomerCard } from '../components/CustomerCard';

/* ------------------------------------------------------------------ */
/* Globale Styles – TaskBoard-Action-Buttons ausblenden               */
/* ------------------------------------------------------------------ */
const GlobalStyle = createGlobalStyle`
  .customer-assignment-modal {
    .k-taskboard-column-header-actions,
    .k-taskboard-card-actions,
    .k-taskboard-card svg[aria-hidden="true"] {
      display: none !important;
    }
  }
`;

/* ------------------------------------------------------------------ */
/* Modal                                                               */
/* ------------------------------------------------------------------ */
const CustomerAssignmentModal = ({
  visible,
  onClose,
  initialCustomer,
  customers             // [{ value, label, details }]
}) => {
  /* ---------------- Spalten -------------------------------------- */
  const [columnsData] = React.useState([
    { id: 1, title: 'Kunde',              status: 'customer' },
    { id: 2, title: 'Ausgewählter Kunde', status: 'assigned' }
  ]);

  /* ---------------- Tasks: aus Kunden aufbauen ------------------- */
  const buildTasks = React.useCallback(
    () =>
      customers.map((c) => ({
        id       : c.value,
        title    : c.details?.Firma || c.label || '',
        street   : `${c.details?.Strasse || ''} ${c.details?.Hausnummer || ''}`.trim(),
        city     : `${c.details?.PLZ || ''} ${c.details?.ORT || ''}`.trim(),
        firstName: c.details?.Vorname || '',
        lastName : c.details?.Nachname || '',
        status   : c.value === initialCustomer ? 'assigned' : 'customer',
        priority : { color: 'gray', priority: 'None' }
      })),
    [customers, initialCustomer]
  );

  const [taskData, setTaskData] = React.useState(buildTasks);

  /* ---------------- Filter-State --------------------------------- */
  const [filter, setFilter] = React.useState('');

  /* -------- Reset bei jedem Öffnen des Modals -------------------- */
  React.useEffect(() => {
    if (visible) {
      setFilter('');            // Suchfeld leeren
      setTaskData(buildTasks()); // Liste neu erzeugen
    }
  }, [visible, buildTasks]);

  /* ---------------- Drag-&-Drop ---------------------------------- */
  const onChangeHandler = React.useCallback(
    (e) => { if (e.type !== 'column') setTaskData(e.data); },
    []
  );

  /* ---------------- Live-gefilterte Sicht ------------------------ */
  const resultTasks = React.useMemo(() => {
    if (!filter.trim()) return taskData;
    const f = filter.trim().toLowerCase();

    return taskData.filter((t) =>
      t.status === 'assigned' ||
      (t.status === 'customer' && (
        (t.title      || '').toLowerCase().includes(f) ||
        (t.description|| '').toLowerCase().includes(f) ||
        (t.street     || '').toLowerCase().includes(f) ||
        (t.city       || '').toLowerCase().includes(f) ||
        (t.firstName  || '').toLowerCase().includes(f) ||
        (t.lastName   || '').toLowerCase().includes(f)
      ))
    );
  }, [filter, taskData]);

  /* ---------------- Speichern ------------------------------------ */
  const saveCustomer = () => {
    const chosen = taskData.find((t) => t.status === 'assigned');
    onClose(chosen ? { value: chosen.id, label: chosen.title, details: chosen } : null);
  };

  /* ---------------- Render --------------------------------------- */
  if (!visible) return null;

  return (
    <>
      <GlobalStyle />
      <Dialog
        title="Kunde auswählen"
        onClose={() => onClose(null)}
        width={730}
        className="customer-assignment-modal"
      >
        <div style={{ padding: 20 }}>
          <TaskBoard
            columnData={columnsData}
            taskData={resultTasks}
            onChange={onChangeHandler}
            card={CustomerCard}
            style={{ height: 400 }}
            tabIndex={0}
          >
            <TaskBoardToolbar>
              <KInput
                placeholder="Suchen …"
                value={filter}
                onChange={(e) => setFilter(e.value)}
                style={{ width: 260 }}
              />
              <span className="k-spacer" />
            </TaskBoardToolbar>
          </TaskBoard>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
            <Button onClick={() => onClose(null)}>Abbrechen</Button>
            <Button themeColor="primary" onClick={saveCustomer}>Speichern</Button>
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default CustomerAssignmentModal;
